import {
  buttonOrange,
  buttonWhite,
  orangeShades,
  primaryColors,
  transparentColors,
  whiteShades
} from '@assets/styles/helpers/colors.styles'
import { datePickerPopperStyles } from '../DatePicker/DatePicker.styles'

/**
 * The date picker's popper plus the one view it never renders: the clock.
 *
 * `datePickerPopperStyles` covers the paper, the day grid and the year list, so
 * it is spread rather than repeated, everything below only concerns
 * `MultiSectionDigitalClock`, which the date picker has no view for and which
 * therefore still came out in MUI's stock theme (blue selection, Roboto).
 */
export const timePickerPopperStyles = {
  ...datePickerPopperStyles,

  /*
    The action bar (Cancel / OK) spans all three layout columns and is wider
    than the two clock columns, so the surplus width settles in column 1, an
    empty gap down the left of the popup. The content starts at column 2 by
    default; spanning it across all three and letting the sections share the
    width removes the gap instead of moving it to the other side.
  */
  '& .MuiPickersLayout-contentWrapper': {
    gridColumn: '1 / 4'
  },
  /*
    `datePickerPopperStyles` paints every `MuiButtonBase` inside the popper
    white, which is what MUI's own arrows and switch-view button need on this
    dark surface. The action bar's buttons are the app's own and carry a fill,
    so that blanket rule turned the confirm button into a white label on a
    white background. Restated here from the tokens `Button` itself uses,
    because the popper's descendant selector outranks the `sx` on the button.
  */
  '& .MuiPickersLayout-actionBar .MuiButton-containedPrimary': {
    color: buttonWhite.contained.font
  },
  '& .MuiPickersLayout-actionBar .MuiButton-containedSecondary': {
    color: buttonOrange.contained.font
  },
  '& .MuiMultiSectionDigitalClock-root': {
    width: '100%',
    // MUI draws a divider above the action bar; the buttons carry their own
    // fill, so a rule under them only adds noise.
    borderBottom: 'none',
    // Anchors the separator below.
    position: 'relative',
    /*
      The column separator, drawn here rather than on a column.

      A column is its own scroll container, and an absolutely positioned child
      of one scrolls with the content: the line rode up with the numbers and
      ended part way down the popup. On the container it stays put.

      `left: 50%` because the two sections are `flex: 1` and the clock is fixed
      to 24-hour, so there are always exactly two of them and the boundary is
      the middle. Held off both ends and capped at half its width, so it reads
      as a drawn rule rather than a box edge.
    */
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '0.5rem',
      bottom: '0.5rem',
      left: '50%',
      marginLeft: '-1px',
      width: '2px',
      borderRadius: '1px',
      backgroundColor: whiteShades.shade1,
      // Purely decorative, and it sits over the columns.
      pointerEvents: 'none'
    }
  },
  // MUI's ripple is not used anywhere in this app. The clock items are the
  // library's own MenuItems, so it is switched off through the slot as well -
  // this covers whatever the slot does not reach.
  '& .MuiTouchRipple-root': {
    display: 'none'
  },
  '& .MuiMultiSectionDigitalClockSection-root': {
    flex: 1,
    width: 'auto',
    /*
      No bar at all. The native one takes width off a column barely wider than
      the numbers in it, which shifted them sideways the moment the pointer
      arrived, and an overlay one is a full-height stripe down a list a few
      items tall. The column still scrolls by wheel, drag and keyboard.

      Hidden twice over: `scrollbar-width` for Firefox and current Chrome, the
      pseudo-element for older WebKit. MUI only turns the overflow on while the
      pointer is over the column, so it is set here outright as well.
    */
    overflowY: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    /*
      MUI also draws its own 1px divider on the left of every column after the
      first. The separator is a rounded rule on the container now, so this one
      is a second line in the same place.
    */
    '&:not(:first-of-type)': {
      borderLeft: 'none'
    }
  },
  '& .MuiMultiSectionDigitalClockSection-item': {
    width: 'auto',
    borderRadius: '0.25rem',
    fontFamily: 'helvetica-neue-lt-pro, sans-serif',
    fontWeight: 400,
    color: primaryColors.white,
    // Outline rather than a border on hover, as the year buttons do, a border
    // would shift the number by two pixels every time the pointer passes.
    '&:hover': {
      backgroundColor: transparentColors.transparent,
      outline: `2px solid ${transparentColors.whiteShade2} !important`
    },
    '&:active': {
      outline: `2px solid ${transparentColors.whiteShade1} !important`
    },
    '&.Mui-focusVisible': {
      backgroundColor: transparentColors.transparent
    },
    '&.Mui-disabled': {
      color: `${whiteShades.shade2} !important`
    },
    '&.Mui-selected': {
      backgroundColor: `${orangeShades.shade1} !important`,
      color: primaryColors.white,
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: `${orangeShades.shade2} !important`
      }
    }
  }
}
